/**
 * Drink-by state (T099, FR-033, FR-041, FR-037, data-model.md "Drink-by state"): the due date, the
 * five reminder dates, the stage 0–5 that drives the card colour, and the "à boire bientôt" /
 * "dépassé" badge. Shared with the monthly digest (User Story 6, `functions/digest`), so the email,
 * the badge and the colour always agree.
 *
 * 1. Due date: 31 December of the drink-until year (the month is gone since schema version 6,
 *    owner 2026-10-06).
 * 2. Reminder dates: last day of the month 36, 24, 12, 6 and 0 months before the due month.
 * 3. Stage: how many reminder dates are reached (today on or after them), 0 to 5.
 * 4. Badge: stage 4 or 5 and not yet past the due date → "soon"; after the due date → "past".
 * 5. No drink-until value, or no bottles left: stage 0, no badge.
 * "Today" is the Europe/Paris calendar day.
 */
import { wineStock, type Wine } from './cellar';
import { monthsBefore, todayInParis, type CalendarDay } from './months';

// Moved to months.ts (2026-10-06, purchases keep their day); still exported from here.
export { todayInParis, type CalendarDay };

export type DrinkByStage = 0 | 1 | 2 | 3 | 4 | 5;

export interface DrinkByState {
  stage: DrinkByStage;
  badge?: 'soon' | 'past';
  due?: CalendarDay;
}

/** Months before the due date at which a reminder date falls. */
export const REMINDER_OFFSETS_MONTHS = [36, 24, 12, 6, 0] as const;

const lastDayOf = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

const compareDays = (a: CalendarDay, b: CalendarDay) =>
  a.year - b.year || a.month - b.month || a.day - b.day;

export function dueDate(wine: Pick<Wine, 'drinkUntilYear'>): CalendarDay | undefined {
  if (wine.drinkUntilYear === undefined) return undefined;
  return { year: wine.drinkUntilYear, month: 12, day: 31 };
}

export function reminderDates(due: CalendarDay): CalendarDay[] {
  return REMINDER_OFFSETS_MONTHS.map((offset) => {
    const { year, month } = monthsBefore({ year: due.year, month: due.month }, offset);
    return { year, month, day: lastDayOf(year, month) };
  });
}

export function drinkByState(wine: Wine, today: CalendarDay = todayInParis()): DrinkByState {
  const due = dueDate(wine);
  if (!due || wineStock(wine) === 0) return { stage: 0 };

  const stage = reminderDates(due).filter((date) => compareDays(today, date) >= 0)
    .length as DrinkByStage;
  const badge =
    compareDays(today, due) > 0 ? ('past' as const) : stage >= 4 ? ('soon' as const) : undefined;
  return { stage, ...(badge ? { badge } : {}), due };
}

/** In-stock wines due soon or past their window: the count indicator (FR-037). */
export function countDueWines(wines: readonly Wine[], today: CalendarDay = todayInParis()): number {
  return wines.filter((wine) => drinkByState(wine, today).badge !== undefined).length;
}
